import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "bootstrap-icons/font/bootstrap-icons.css";
import { useNavigate, useParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";


const StripePaymentForm = ({ guest, refreshGuest, isRecurring }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);

    const { error: methodErr, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardNumberElement),
      billing_details: {
        name: guest.name,
        email: guest.email,
        phone: guest.phone,
      },
    });

    if (methodErr) {
      Swal.fire("Error", methodErr.message, "error");
      setLoading(false);
      return;
    }

    try {
      let url =
        isRecurring == 1
          ? `${process.env.REACT_APP_WEB_SERVICES_URL}/admin/guest-subscription-payment`
          : `${process.env.REACT_APP_WEB_SERVICES_URL}/admin/guest-one-time-payment`;

      const res = await axios.post(
        url,
        {
          guest_id: guest.id,
          payment_method_id: paymentMethod.id,
        },
        { withCredentials: true }
      );

      if (res.data.status) {
        Swal.fire("Success", "Payment successful!", "success");
        navigate(`/admin/guest-management/guest`);
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  // --- Calculations ---
  let total_discount = 0;
  guest?.monthly_rents?.forEach((rent) => {
    total_discount += Number(rent.discount || 0);
  });
  const total_bed_charges =
    guest?.monthly_rate * guest?.monthly_rents?.length - total_discount;

  let total_rate = 0;
  let total_rate_discount = 0;
  guest?.additional_items?.forEach((item) => {
    total_rate += Number(item.rate || 0);
    total_rate_discount += Number(item.discount || 0);
  });
  const total_rate_after_discount = total_rate - total_rate_discount;
  const total_amount = total_bed_charges + total_rate_after_discount;

  return (
    <form onSubmit={handleSubmit}>
      <div className="border rounded-2 p-3 bg-light">
        <div className="row mb-2">
          <div className="col-sm-6 fw-bold">Name:</div>
          <div className="col-sm-6">{guest?.name}</div>
        </div>
        <div className="row mb-2">
          <div className="col-sm-6 fw-bold">Email:</div>
          <div className="col-sm-6">{guest?.email}</div>
        </div>

        <div className="row mb-2">
          <div className="col-sm-6 fw-bold">Bed Charges:</div>
          <div className="col-sm-6">
            ${guest?.monthly_rate} × {guest?.monthly_rents?.length} months = $
            {guest?.monthly_rate * guest?.monthly_rents?.length}
          </div>
        </div>

        <div className="row mb-2">
          <div className="col-sm-6 fw-bold">Bed Discount:</div>
          <div className="col-sm-6">${total_discount}</div>
        </div>

        <div className="row mb-2">
          <div className="col-sm-6 fw-bold">Additional Items:</div>
          <div className="col-sm-6">${total_rate}</div>
        </div>
        <div className="row mb-2">
          <div className="col-sm-6 fw-bold">Add. Items Discount:</div>
          <div className="col-sm-6">${total_rate_discount}</div>
        </div>

        <div className="row mb-2">
          <div className="col-sm-12 fw-bold">
            Enroll this card holder/account holder in autopay ${total_bed_charges} every{" "}
            {guest?.monthly_rents?.length} months
          </div>
        </div>

        <hr />
        <div className="row mb-2">
          <div className="col-sm-6 fw-bold">Total Amount:</div>
          <div className="col-sm-6 h5 mb-0">${total_amount}</div>
        </div>
      </div>

      <div className="my-3">
        <label className="form-label">Card Number</label>
        <CardNumberElement className="form-control p-2 border mb-3" />
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label">Expiry</label>
          <CardExpiryElement className="form-control p-2 border" />
        </div>
        <div className="col-md-6 mb-3">
          <label className="form-label">CVC</label>
          <CardCvcElement className="form-control p-2 border" />
        </div>
      </div>

      <div className="d-flex flex-wrap gap-3 justify-content-center">
        <button
          type="button"
          className="btn btn-lg btn-outline-dark"
          onClick={() =>
            navigate(`/admin/guest-management/guest-intake-form-review/${guest?.id}`)
          }
        >
          Go Back
        </button>
        <button type="submit" className="btn btn-dark btn-lg" disabled={loading}>
          {loading ? "Loading: Please Wait..." : "Pay $" + total_amount}
        </button>
      </div>
    </form>
  );
};

const GuestIntakeFormPayment = () => {
  const { id, is_recurring } = useParams();
  const [loading, setLoading] = useState(false);
  const [guest, setGuest] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    // ✅ Lazy-load Stripe only when this page is visited
    setStripePromise(loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY));
  }, []);

  const getGuest = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_WEB_SERVICES_URL}/admin/get-guest/${id}`,
        { withCredentials: true }
      );
      setGuest(res.data?.guest || {});
    } catch (err) {
      Swal.fire("Error", "Failed to load guest information", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getGuest();
  }, []);

  if (loading || !stripePromise || !guest) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4 mt-4 mb-5" id="guest-main">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Guest Intake Form</h3>
      </div>

      <div className="row mb-4">
        <div className="col-lg-3 fw-bold">
          <i className="bi bi-briefcase-fill me-2"></i>Details
        </div>
        <div className="col-lg-3 fw-bold">
          <i className="bi bi-person-fill me-2"></i>Move-In
        </div>
        <div className="col-lg-3 fw-bold">
          <i className="bi bi-file-earmark-check-fill me-2"></i>Review
        </div>
        <div className="col-lg-3 fw-bold">
          <i className="bi bi-credit-card-fill me-2"></i>Pay
        </div>
      </div>

      <div className="row mb-4 justify-content-center">
        <div className="col-lg-4 col-md-16 col-sm-12">
          <div className="card shadow mt-4">
            <div className="card-header">
              <h5 className="mb-0">Pay with Credit Card</h5>
            </div>
            <div className="card-body p-4">
              {guest.stripe_subscription_id ? (
                <div className="alert alert-success text-center">
                  <h5 className="mb-1">Subscription Active</h5>
                  <p className="mb-0">
                    Status:{" "}
                    <strong>{guest.subscription_status || "active"}</strong>
                  </p>
                  <p className="mb-0">
                    Stripe ID: <code>{guest.stripe_subscription_id}</code>
                  </p>
                </div>
              ) : guest.stripe_payment_method_id ? (
                <div className="alert alert-success text-center">
                  <h5 className="mb-1">Paid</h5>
                </div>
              ) : (
                <Elements stripe={stripePromise}>
                  <StripePaymentForm
                    guest={guest}
                    refreshGuest={getGuest}
                    isRecurring={is_recurring}
                  />
                </Elements>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestIntakeFormPayment;
